// Table_4.jsx : 거래처 검색

import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import "../../styles/Table.css"; // Table_1 스타일 재사용

const Table_4 = ({
  clientList,
  setUserId,
  orderBookReset,
  clientQuery,
  setClientQuery,
  columnWidths = ["210px", "80px"],
  columnAligns = ["center", "center"],
}) => {
  const [selectedClientCode, setSelectedClientCode] = useState(null);
  const [filteredList, setFilteredList] = useState(clientList);

  const tableContainerRef = useRef(null);
  const selectedRowRef = useRef(null);

  const escapeRegExp = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  };

  const highlightText = (text, query) => {
    if (!query) return text;
    const plainText = typeof text === "string" ? text : String(text);
    const safeQuery = escapeRegExp(query);
    const regex = new RegExp(`(${safeQuery})`, "gi");
    const testRegex = new RegExp(`^${safeQuery}$`, "i");
    const parts = plainText.split(regex);
    return parts.map((part, idx) =>
      testRegex.test(part) ? <mark key={idx}>{part}</mark> : part,
    );
  };
  // --- 순서 변경 시작 ---
  // 1. 검색 키워드 및 필터링된 리스트 계산 (먼저 수행)
  const keyword = useMemo(
    () => (clientQuery || "").trim().toLowerCase(),
    [clientQuery],
  );

  const handleQueryInputChange = (e) => {
    setClientQuery(e.target.value);
  };

  useEffect(() => {
    if (!keyword) {
      setFilteredList([]);
      return;
    }
    const updatedList = clientList.filter((item) => {
      const fieldsToSearch = [
        item?.ClientCode || "",
        item?.ClientName || "",
        item?.DeputyName || "",
      ];
      return fieldsToSearch.some((field) =>
        field.toLowerCase().includes(keyword),
      );
    });
    setFilteredList(updatedList);
  }, [keyword, clientList]);

  const handleRowClick = useCallback(
    (data) => {
      setSelectedClientCode(data.ClientCode);
      setUserId(data.ClientCode);
      orderBookReset();
    },
    [setUserId, orderBookReset],
  );

  // 선택 행이 바뀔 때 스크롤로 화면에 보이도록 수동 계산
  useEffect(() => {
    const row = selectedRowRef.current;
    const container = tableContainerRef.current;
    if (!row || !container) return;

    // sticky 헤더 높이 측정
    const thead = container.querySelector("thead");
    const theadHeight = thead ? thead.offsetHeight : 0;

    const rowTop = row.offsetTop;
    const rowBottom = rowTop + row.offsetHeight;
    const visibleTop = container.scrollTop + theadHeight;
    const visibleBottom = container.scrollTop + container.clientHeight;

    if (rowTop < visibleTop) {
      container.scrollTop = rowTop - theadHeight;
    } else if (rowBottom > visibleBottom) {
      container.scrollTop = rowBottom - container.clientHeight;
    }
  }, [selectedClientCode]);

  // Up/Down 키 핸들러
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
      e.preventDefault();

      const list = Array.isArray(filteredList) ? filteredList : [];
      const currentIndex = list.findIndex(
        (item) => item.ClientCode === selectedClientCode,
      );

      let nextIndex;
      if (currentIndex === -1) {
        nextIndex = 0;
      } else if (e.key === "ArrowUp") {
        nextIndex = Math.max(0, currentIndex - 1);
      } else {
        nextIndex = Math.min(list.length - 1, currentIndex + 1);
      }

      if (nextIndex === currentIndex && currentIndex !== -1) return;

      const nextItem = list[nextIndex];
      if (!nextItem) return;

      handleRowClick(nextItem);
    },
    [filteredList, selectedClientCode, handleRowClick],
  );

  return (
    <>
      {/* 검색 영역: 스크롤 영역 밖에 있도록 기존 블록구조로 복원 */}
      <div className="form-wrapper">
        <div className="input-row">
          <label className="input-label">거래처검색</label>
          <input
            className="input-box query-input-narrow"
            type="text"
            name="queryBox"
            placeholder="회사명/ 대표자 입력"
            value={clientQuery}
            onChange={handleQueryInputChange}
            autoComplete="off"
          />
        </div>
      </div>

      {/* 테이블만 스크롤 되도록 구현 & 단일 리스트 렌더링 (페이지네이션 제거) */}
      <div
        className="scroll-table-wrapper"
        ref={tableContainerRef}
        tabIndex={0}
        onKeyDown={handleKeyDown}
        onFocus={(e) => (e.currentTarget.style.outline = "none")}
        style={{ overflowY: "auto", maxHeight: "205px", outline: "none" }}
      >
        <table className="table scroll table-hover">
          <thead>
            <tr>
              {["회사명", "대표자"].map((header, idx) => (
                <th
                  key={idx}
                  style={{
                    width: columnWidths[idx],
                    textAlign: columnAligns[idx],
                  }}
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(filteredList || [])
              .filter((d) => d && d.ClientCode)
              .map((d) => {
                const isSelected = selectedClientCode === d.ClientCode;
                return (
                  <tr
                    key={d.ClientCode}
                    ref={isSelected ? selectedRowRef : null}
                    onClick={() => handleRowClick(d)}
                    className={isSelected ? "selected-row" : ""}
                  >
                    <td style={{ textAlign: columnAligns[0] }}>
                      {highlightText(d.ClientName, keyword)}
                    </td>
                    <td style={{ textAlign: columnAligns[1] }}>
                      {highlightText(d.DeputyName, keyword)}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default Table_4;
